/**
 * Parses a FastAPI/Pydantic error response body into a human-readable string.
 *
 * FastAPI returns two shapes:
 *   - String detail:  { "detail": "some message" }
 *   - Array detail (Pydantic 422): { "detail": [{ "loc": ["body", "name"], "msg": "...", "type": "...", "ctx": {...} }] }
 */
export function parseApiError(errorData: unknown, fallback: string): string {
  if (!errorData || typeof errorData !== 'object') return fallback;
  const detail = (errorData as Record<string, unknown>).detail;

  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item: unknown) => {
        if (!item || typeof item !== 'object') return fallback;
        const { loc, msg, type, ctx } = item as {
          loc?: unknown[];
          msg?: string;
          type?: string;
          ctx?: Record<string, unknown>;
        };
        const field = Array.isArray(loc)
          ? loc.filter(p => p !== 'body').map(p => String(p)).join(' → ')
          : '';
        const label = field ? toLabel(field) : '';
        return friendlyMessage(label, type, ctx, msg ?? fallback);
      })
      .join('; ');
  }

  return fallback;
}

function friendlyMessage(
  label: string,
  type: string | undefined,
  ctx: Record<string, unknown> | undefined,
  raw: string,
): string {
  const prefix = label ? `${label} ` : '';
  switch (type) {
    case 'string_too_long':
      return `${prefix}is too long (max ${ctx?.max_length ?? ''} characters)`.trim();
    case 'string_too_short':
      return `${prefix}is too short (min ${ctx?.min_length ?? ''} characters)`.trim();
    case 'missing':
      return `${prefix}is required`.trim();
    default:
      return label ? `${label}: ${raw}` : raw;
  }
}

function toLabel(field: string): string {
  const words = field.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
